import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Shield,
  User,
  Mail,
  Lock,
  Save,
  X,
  QrCode,
  Key,
  AlertTriangle,
  Check,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: "admin", label: "Administrador", description: "Acceso completo" },
  { value: "user", label: "Usuario", description: "Sin acceso a gestión de usuarios" },
];

export default function UsersPage() {
  const { user: currentUser, isAdmin, setup2FA, enable2FA, disable2FA } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Estado 2FA
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorVerifyCode, setTwoFactorVerifyCode] = useState("");
  const [twoFactorDisableCode, setTwoFactorDisableCode] = useState("");
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [showTwoFactorDisable, setShowTwoFactorDisable] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("No tienes permisos para acceder a esta página");
      navigate("/");
      return;
    }
    loadUsers();
  }, [isAdmin, navigate]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingUser) {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, updateData);
        toast.success("Usuario actualizado");
      } else {
        // Create new user
        if (!formData.password) {
          toast.error("La contraseña es requerida");
          setSubmitting(false);
          return;
        }
        await axios.post(`${API_URL}/api/users`, formData);
        toast.success("Usuario creado");
      }
      setDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al guardar usuario";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`);
      toast.success("Usuario eliminado");
      loadUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al eliminar usuario";
      toast.error(message);
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "user",
    });
  };

  // Funciones 2FA
  const handleSetup2FA = async () => {
    setTwoFactorLoading(true);
    try {
      const setupData = await setup2FA();
      setTwoFactorSetup(setupData);
      setShowTwoFactorSetup(true);
      toast.success("2FA configurado. Escanea el QR code");
    } catch (error) {
      toast.error("Error al configurar 2FA");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!twoFactorVerifyCode) {
      toast.error("Ingresa el código de verificación");
      return;
    }

    setTwoFactorLoading(true);
    try {
      await enable2FA(twoFactorVerifyCode);
      toast.success("2FA habilitado correctamente");
      setShowTwoFactorSetup(false);
      setTwoFactorSetup(null);
      setTwoFactorVerifyCode("");
      loadUsers();
    } catch (error) {
      toast.error("Código de verificación inválido");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFactorDisableCode) {
      toast.error("Ingresa el código de verificación");
      return;
    }

    setTwoFactorLoading(true);
    try {
      await disable2FA(twoFactorDisableCode);
      toast.success("2FA deshabilitado correctamente");
      setShowTwoFactorDisable(false);
      setTwoFactorDisableCode("");
      loadUsers();
    } catch (error) {
      toast.error("Código de verificación inválido");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-primary">
            Usuarios
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los usuarios del sistema
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-user-btn"
              className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 gap-2"
            >
              <Plus className="w-4 h-4" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingUser ? "Editar Usuario" : "Nuevo Usuario"}
              </DialogTitle>
              <DialogDescription>
                {editingUser 
                  ? "Modifica los datos del usuario. Deja la contraseña vacía para mantener la actual."
                  : "Crea un nuevo usuario para acceder al sistema"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-11 rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-11 rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  Contraseña {editingUser ? "(dejar vacío para no cambiar)" : "*"}
                </Label>
                <Input
                  id="password"
                  data-testid="user-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-11 rounded-xl"
                  required={!editingUser}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rol *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select" className="h-11 rounded-xl">
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          {role.value === "admin" ? (
                            <Shield className="w-4 h-4 text-secondary" />
                          ) : (
                            <User className="w-4 h-4" />
                          )}
                          <span>{role.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {ROLES.find(r => r.value === formData.role)?.description}
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                  className="rounded-full"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="user-submit-btn"
                  className="rounded-full bg-primary gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    "Guardando..."
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingUser ? "Actualizar" : "Crear Usuario"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-soft bg-gradient-to-r from-primary/5 to-secondary/5">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-secondary rounded-xl">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-primary">Roles de Usuario</h3>
              <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                <p><strong>Administrador:</strong> Acceso completo, puede gestionar usuarios</p>
                <p><strong>Usuario:</strong> Acceso a consultas, clientes y API, sin gestión de usuarios</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Card - Solo para usuario actual */}
      {currentUser && (
        <Card className="border-0 shadow-soft bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-500 rounded-xl">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-primary">Autenticación de Dos Factores (2FA)</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Protege tu cuenta con un código de seguridad adicional
                    </p>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={currentUser.two_factor_enabled 
                      ? "bg-green-100 text-green-700 border-green-300" 
                      : "bg-yellow-100 text-yellow-700 border-yellow-300"
                    }
                  >
                    {currentUser.two_factor_enabled ? "Habilitado" : "Deshabilitado"}
                  </Badge>
                </div>
                
                {currentUser.two_factor_enabled ? (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="w-4 h-4" />
                      <span className="text-sm">Tu cuenta está protegida con 2FA</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTwoFactorDisable(true)}
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    >
                      Deshabilitar 2FA
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 text-yellow-600">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm">Tu cuenta no está protegida con 2FA</span>
                    </div>
                    <Button
                      onClick={handleSetup2FA}
                      disabled={twoFactorLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {twoFactorLoading ? "Configurando..." : "Habilitar 2FA"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de configuración 2FA */}
      {showTwoFactorSetup && twoFactorSetup && (
        <Dialog open={showTwoFactorSetup} onOpenChange={setShowTwoFactorSetup}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                Configurar 2FA
              </DialogTitle>
              <DialogDescription>
                Escanea el QR code con Google Authenticator o Authy
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="flex justify-center">
                <img 
                  src={`data:image/png;base64,${twoFactorSetup.qr_code}`}
                  alt="QR Code para 2FA"
                  className="border-2 border-gray-200 rounded-lg p-2"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Código de verificación</Label>
                <Input
                  value={twoFactorVerifyCode}
                  onChange={(e) => setTwoFactorVerifyCode(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                />
              </div>

              <div className="space-y-2">
                <Label>Códigos de Backup</Label>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">
                    Guarda estos códigos en un lugar seguro. Solo puedes usarlos una vez.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                    {twoFactorSetup.backup_codes.map((code, index) => (
                      <div key={index} className="bg-white p-2 rounded border">
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                onClick={handleEnable2FA}
                disabled={twoFactorLoading || !twoFactorVerifyCode}
                className="w-full"
              >
                {twoFactorLoading ? "Verificando..." : "Habilitar 2FA"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal para deshabilitar 2FA */}
      {showTwoFactorDisable && (
        <Dialog open={showTwoFactorDisable} onOpenChange={setShowTwoFactorDisable}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Deshabilitar 2FA</DialogTitle>
              <DialogDescription>
                Ingresa el código de tu aplicación autenticadora para deshabilitar 2FA.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Código de 2FA</Label>
                <Input
                  value={twoFactorDisableCode}
                  onChange={(e) => setTwoFactorDisableCode(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowTwoFactorDisable(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDisable2FA}
                  disabled={twoFactorLoading || !twoFactorDisableCode}
                  className="flex-1"
                >
                  {twoFactorLoading ? "Verificando..." : "Deshabilitar"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Users List */}
      <div className="grid gap-4">
        {users.map((user) => (
          <Card key={user.id} className="border-0 shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    user.role === "admin" ? "bg-secondary/10" : "bg-primary/10"
                  }`}>
                    {user.role === "admin" ? (
                      <Shield className="w-6 h-6 text-secondary" />
                    ) : (
                      <User className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-primary">{user.name}</h3>
                      <Badge 
                        variant="outline" 
                        className={user.role === "admin" 
                          ? "bg-secondary/10 text-secondary border-secondary/30" 
                          : ""
                        }
                      >
                        {ROLES.find(r => r.value === user.role)?.label || user.role}
                      </Badge>
                      {user.id === currentUser?.id && (
                        <Badge variant="outline" className="bg-primary/10 text-primary">
                          Tú
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid={`edit-user-${user.id}`}
                    onClick={() => handleEdit(user)}
                    className="rounded-full"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {user.id !== currentUser?.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10 rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Se eliminará el usuario "{user.name}" permanentemente. Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(user.id)}
                            className="rounded-full bg-destructive"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {users.length === 0 && (
        <Card className="border-0 shadow-soft">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-heading text-xl font-semibold text-primary mb-2">
              No hay usuarios
            </h3>
            <p className="text-muted-foreground">
              Crea el primer usuario para comenzar
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
